import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Tag, Percent, DollarSign, 
  Calendar, ToggleLeft, ToggleRight
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_amount: 0,
    max_uses: 0,
    valid_from: '',
    valid_until: '',
    description: ''
  });

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/admin/campaigns`, { withCredentials: true });
      setCampaigns(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Fetch campaigns error:', error);
      toast.error('Kampanyalar yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.code || !createForm.discount_value) {
      toast.error('Kampanya kodu ve indirim miktarı gerekli');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/admin/campaigns`, createForm, { withCredentials: true });
      toast.success('Kampanya oluşturuldu');
      setShowCreateDialog(false);
      setCreateForm({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_amount: 0,
        max_uses: 0,
        valid_from: '',
        valid_until: '',
        description: ''
      });
      fetchCampaigns();
    } catch (error) {
      console.error('Create campaign error:', error);
      toast.error(error.response?.data?.detail || 'Kampanya oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (campaign) => {
    try {
      await axios.put(
        `${API}/admin/campaigns/${campaign.campaign_id}`,
        { is_active: !campaign.is_active },
        { withCredentials: true }
      );
      toast.success('Kampanya durumu güncellendi');
      fetchCampaigns();
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = (campaign) => {
    setDeletingCampaign(campaign);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/campaigns/${deletingCampaign.campaign_id}`,
        { withCredentials: true }
      );
      toast.success('Kampanya silindi');
      setShowDeleteDialog(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kampanyalar</h1>
          <p className="text-sm text-slate-600 mt-1">
            Toplam {campaigns.length} kampanya • {campaigns.filter(c => c.is_active).length} Aktif
          </p>
        </div>
        <Button 
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={() => setShowCreateDialog(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kampanya
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kampanya Kodu</TableHead>
                <TableHead>İndirim</TableHead>
                <TableHead>Min. Tutar</TableHead>
                <TableHead>Kullanım</TableHead>
                <TableHead>Geçerlilik</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Henüz kampanya yok
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow key={campaign.campaign_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-600" />
                        <span className="font-mono font-bold">{campaign.code}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {campaign.discount_type === 'percentage' ? (
                          <>
                            <Percent className="w-3 h-3" />
                            <span>{campaign.discount_value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-3 h-3" />
                            <span>{campaign.discount_value} ₺</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{campaign.min_amount} ₺</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {campaign.current_uses || 0} / {campaign.max_uses || '∞'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-slate-600">
                        {campaign.valid_from && format(new Date(campaign.valid_from), 'd MMM', { locale: tr })}
                        {' - '}
                        {campaign.valid_until && format(new Date(campaign.valid_until), 'd MMM', { locale: tr })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(campaign)}
                        className="gap-1"
                      >
                        {campaign.is_active ? (
                          <>
                            <ToggleRight className="w-4 h-4 text-green-600" />
                            <span className="text-green-600 text-xs">Aktif</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-400 text-xs">Pasif</span>
                          </>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(campaign)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Yeni Kampanya Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Kampanya Kodu *</Label>
                <Input
                  placeholder="YENI2026"
                  value={createForm.code}
                  onChange={(e) => setCreateForm({...createForm, code: e.target.value.toUpperCase()})}
                  className="font-mono"
                />
              </div>

              <div>
                <Label>İndirim Tipi *</Label>
                <Select value={createForm.discount_type} onValueChange={(value) => setCreateForm({...createForm, discount_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Yüzde (%)</SelectItem>
                    <SelectItem value="fixed">Sabit Tutar (₺)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>İndirim Miktarı *</Label>
                <Input
                  type="number"
                  placeholder="10"
                  value={createForm.discount_value}
                  onChange={(e) => setCreateForm({...createForm, discount_value: Number(e.target.value)})}
                />
              </div>

              <div>
                <Label>Min. Sepet Tutarı (₺)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={createForm.min_amount}
                  onChange={(e) => setCreateForm({...createForm, min_amount: Number(e.target.value)})}
                />
              </div>

              <div>
                <Label>Max. Kullanım (0 = Sınırsız)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={createForm.max_uses}
                  onChange={(e) => setCreateForm({...createForm, max_uses: Number(e.target.value)})}
                />
              </div>

              <div>
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={createForm.valid_from}
                  onChange={(e) => setCreateForm({...createForm, valid_from: e.target.value})}
                />
              </div>

              <div>
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={createForm.valid_until}
                  onChange={(e) => setCreateForm({...createForm, valid_until: e.target.value})}
                />
              </div>

              <div className="col-span-2">
                <Label>Açıklama</Label>
                <Textarea
                  placeholder="Kampanya açıklaması..."
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Oluşturuluyor...' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kampanyayı Sil</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              <strong className="font-mono">{deletingCampaign?.code}</strong> kampanyasını silmek istediğinize emin misiniz?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Siliniyor...' : 'Evet, Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsPage;
